import api from "./parts/create";
import apiEditor from "./parts/createEditor";
import store from "@/store/index";
import router from "@/router";
import { MERCHANTS_PER_PAGE } from "@/config";

enum ERegion {
  DE = "DE",
  BR = "BR",
  NL = "NL",
  ES = "ES",
  IT = "IT",
  FR = "FR"
}

class CApi {
  lang: string;
  currentRegion: ERegion;

  constructor() {
    this.initStoreChangeSubscribe();
  }

  regionChange() {
    const _lang = this.lang.toLowerCase();
    let lang = "NL";
    if (_lang === "br") {
      lang = "BR";
    }
    if (_lang === "de") {
      lang = "DE";
    }
    if (_lang === "es") {
      lang = "ES";
    }
    if (_lang === "it") {
      lang = "IT";
    }
    if (_lang === "fr") {
      lang = "FR";
    }
    this.currentRegion = ERegion[lang];
  }

  initStoreChangeSubscribe() {
    store.subscribe(({ type, payload }) => {
      if (type === "app/setCurrentLang") {
        this.lang = payload.toUpperCase();
        this.regionChange();
      }
    });
  }

  addRequest(url: string, version = "v1") {
    const get = api(version).get(url);
    get
      .then(({ status, data }) => {
        const isError =
          status !== 200 || ("error" in data && data.error.code !== 600);
        if (isError) {
          store.dispatch("app/showRequestError", isError);
        }
      })
      .catch(() => {
        store.dispatch("app/showRequestError");
      });
    return get;
  }

  addRequestEditor(url: string, fn: any) {
    const get = apiEditor().get(url);
    get
      .then(({ status, data }) => {
        const isError = status !== 200 || "error" in data;
        if (isError) {
          store.dispatch("app/showRequestError", isError);
        }
      })
      .catch(() => {
        fn ? fn() : store.dispatch("app/showRequestError");
      });
    return get;
  }

  getArticleCategories() {
    if (!this.currentRegion && !this.lang) {
      return;
    }
    const locale = ["US", "UK"].includes(this.lang)
      ? this.lang
      : this.currentRegion;
    return this.addRequestEditor(`categories?region=${locale}`, null);
  }

  getArticles() {
    const locale = ["US", "UK"].includes(this.lang)
      ? this.lang
      : this.currentRegion;
    return this.addRequestEditor(`main_page/articles?region=${locale}`, null);
  }

  getCategory(id) {
    return this.addRequestEditor(`categories/${id}`, null);
  }

  getCategoryArticles(id, offset, limit) {
    const locale = ["US", "UK"].includes(this.lang)
      ? this.lang
      : this.currentRegion;
    return this.addRequestEditor(
      `categories/${id}/articles?region=${locale}&offset=${offset}&limit=${limit}`,
      null
    );
  }

  getArticle(url) {
    return this.addRequestEditor(`articles/${url}`, () =>
      router.push({ name: "home" })
    );
  }

  getMainData() {
    return this.addRequest(`main?region=${this.currentRegion}`);
  }

  getCategories() {
    const currentlang = store.getters["app/app"].currentLang;
    // const region = currentlang === 'uk' ? 'GB' : this.currentRegion;
    if (currentlang === "uk") {
      return this.addRequest(`categories?region=GB`, "v2");
    }
    if (currentlang === "us") {
      return this.addRequest(`categories?region=US`, "v2");
    } else {
      return this.addRequest(`categories?region=${this.currentRegion}`, "v2");
    }
  }

  getCurrentLocate() {
    return this.addRequest("user-info");
  }

  getCouponsOfMerchant(id, limit, page) {
    return this.addRequest(
      `merchants/${id}/coupons?limit=${limit}&page=${page}&region=${this.currentRegion}`
    );
  }

  getTopCoupons(type, limit, cursor) {
    return this.addRequest(
      `coupons?${type !== "all" ? `query=${type}&` : ""}limit=${limit}${
        cursor ? `&cursor=${cursor}` : ""
      }&region=${this.currentRegion}`
    );
  }

  getMerchants(letter, count) {
    const page = count / MERCHANTS_PER_PAGE - 1; // Pages start from 0
    return this.addRequest(
      `merchants?limit=${MERCHANTS_PER_PAGE}&page=${page}&letter=${letter}&sort=name&region=${this.currentRegion}&containsCouponsOrMonetizedLink=1`
    );
  }

  getOffers({
    category_id,
    length,
    cursor,
    store_name,
    price_from,
    price_to,
    price_sorting,
    direction
  }) {
    const currentlang = store.getters["app/app"].currentLang;
    let lang: any = this.currentRegion;
    if (currentlang === "uk") lang = "GB";
    if (currentlang === "us") lang = "US";
    const params = {
      cursor,
      region: lang,
      store_name,
      price_from,
      price_to,
      price_sorting,
      direction
    };
    // @ts-ignore
    const paramsStr = Object.keys(params).reduce(
      (str, key) =>
        str +
        // @ts-ignore
        (![null, undefined, ""].includes(params[key])
          ? `&${key}=${encodeURI(params[key])}`
          : ""),
      ""
    );
    return this.addRequest(
      `categories/${category_id}/offers?length=${length}` + paramsStr,
      "v2"
    );
  }

  async getStores(category) {
    let currentLang = store.getters["app/app"].currentLang;
    if (currentLang === "uk") {
      return this.addRequest(`categories/${category}/stores?region=GB`, "v2");
    }
    if (currentLang === "us") {
      return this.addRequest(`categories/${category}/stores?region=US`, "v2");
    } else {
      return this.addRequest(
        `categories/${category}/stores?&region=${this.currentRegion}`,
        "v2"
      );
    }
  }
}

const Api = new CApi();

export default Api;
